import React, { useEffect, useMemo, useState } from 'react';
import {
    Avatar, Badge, Box, Dialog, DialogContent, DialogTitle, IconButton, List, ListItem,
    ListItemAvatar, ListItemText, Stack, Typography, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import ApiLink from 'API';
import { useChat } from 'contexts/ChatContext';

// Small green/grey presence dot
function OnlineDot({ online }) {
    return (
        <Box sx={{
            width: 12, height: 12, borderRadius: '50%',
            bgcolor: online ? '#10b981' : '#9e9e9e',
            border: '2px solid #fff',
        }} />
    );
}

export default function MembersDialog({ open, onClose, room }) {
    const { onlineUsers, myId, authHeaders, emit } = useChat();
    const [allStaff, setAllStaff] = useState([]); // for global room

    const isGlobal = room?.type === 'global';

    useEffect(() => {
        if (!open) return;
        emit('chat:get_online'); // refresh presence
        if (isGlobal) {
            axios.get(`${ApiLink}/api/chat/users`, authHeaders())
                .then((r) => setAllStaff(r.data || []))
                .catch(() => setAllStaff([]));
        }
    }, [open, isGlobal]); // eslint-disable-line react-hooks/exhaustive-deps

    const members = useMemo(() => {
        const list = isGlobal ? allStaff : (room?.participants || []);
        // normalize to { _id, name }
        const norm = list.map((u) => ({
            _id: (u._id || u).toString(),
            name: u.fullname || u.username || 'Member',
        }));
        // online first, then by name
        return norm.sort((a, b) => {
            const oa = onlineUsers[a._id] ? 0 : 1;
            const ob = onlineUsers[b._id] ? 0 : 1;
            if (oa !== ob) return oa - ob;
            return a.name.localeCompare(b.name);
        });
    }, [isGlobal, allStaff, room, onlineUsers]);

    const onlineCount = members.filter((m) => onlineUsers[m._id]).length;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                <Stack>
                    <Typography variant="h4">{room?.name || (isGlobal ? '🌐 Global' : 'Members')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {members.length} ສະມາຊິກ · <span style={{ color: '#10b981' }}>{onlineCount} ອອນລາຍ</span>
                    </Typography>
                </Stack>
                <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8 }}><CloseIcon /></IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 0 }}>
                <List dense>
                    {members.map((m) => {
                        const online = !!onlineUsers[m._id];
                        return (
                            <ListItem key={m._id}>
                                <ListItemAvatar>
                                    <Badge
                                        overlap="circular"
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        badgeContent={<OnlineDot online={online} />}
                                    >
                                        <Avatar sx={{ width: 36, height: 36, bgcolor: online ? '#0F5E5C' : '#bdbdbd' }}>
                                            {m.name.charAt(0).toUpperCase()}
                                        </Avatar>
                                    </Badge>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Typography variant="body2" sx={{ fontWeight: online ? 600 : 400 }}>
                                            {m.name}{m._id === myId ? ' (You)' : ''}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography variant="caption" sx={{ color: online ? '#10b981' : 'text.disabled' }}>
                                            {online ? 'ອອນລາຍ' : 'ອອບລາຍ'}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        );
                    })}
                </List>
            </DialogContent>
        </Dialog>
    );
}
